'use client';

import {
  Badge,
  Button,
  Card,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@routly/ui';
import { Database, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { ErrorState, LoadingState, PageHeader } from '@/components/shared';
import { usePrune, useRetention } from '@/hooks/useRetention';

/**
 * What the system keeps.
 *
 * Telemetry expires from ClickHouse after seven days; nothing in Postgres ever
 * expires at all. Events, trips, notifications, webhook deliveries and the
 * audit trail grow with every journey the fleet makes, and no screen said so.
 *
 * Deleting is off unless the server allows it, and even then it is a button
 * rather than a schedule — nothing here should start removing a customer's
 * history because somebody deployed.
 */
export function RetentionPage() {
  const { data, isLoading, error } = useRetention();
  const prune = usePrune();

  const handlePrune = async () => {
    try {
      const { deleted } = await prune.mutateAsync();
      toast.success(`Deleted ${deleted} rows`);
    } catch (caught) {
      toast.error('The prune did not run', {
        description: caught instanceof Error ? caught.message : undefined,
      });
    }
  };

  if (isLoading) return <LoadingState />;
  if (error) return <ErrorState title="Could not read retention" message={error.message} />;
  if (!data) return null;

  return (
    <>
      <PageHeader
        title="What is kept, and for how long"
        description="Raw points expire on a timer; everything else is kept until pruning is switched on."
      />

      <Card className="p-6">
        <div className="flex items-center gap-3">
          <Database className="h-5 w-5 text-muted-foreground" />
          <div>
            <p className="font-medium text-foreground">
              Telemetry · {data.telemetry.days} days in {data.telemetry.store}
            </p>
            <p className="text-sm text-muted-foreground">{data.telemetry.reason}</p>
          </div>
        </div>
      </Card>

      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Data</TableHead>
              <TableHead>Rows</TableHead>
              <TableHead>Oldest</TableHead>
              <TableHead>Window</TableHead>
              <TableHead>Past window</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.tables.map((table) => (
              <TableRow key={table.table}>
                <TableCell>
                  <div className="font-medium text-foreground">{table.table}</div>
                  <p className="text-xs text-muted-foreground">{table.reason}</p>
                </TableCell>
                <TableCell className="tabular-nums text-muted-foreground">
                  {table.rows.toLocaleString()}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {table.oldest ? new Date(table.oldest).toLocaleDateString() : '—'}
                </TableCell>
                <TableCell>
                  {table.days === null ? (
                    <Badge variant="secondary">kept</Badge>
                  ) : (
                    <Badge variant="outline">{table.days} days</Badge>
                  )}
                </TableCell>
                <TableCell className="tabular-nums text-muted-foreground">
                  {table.days === null ? '—' : table.expired.toLocaleString()}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {data.pruningEnabled ? (
        <Button
          variant="destructive"
          disabled={data.expiredTotal === 0 || prune.isPending}
          onClick={handlePrune}
        >
          <Trash2 className="mr-2 h-4 w-4" />
          {prune.isPending ? 'Deleting…' : 'Delete what has expired'}
        </Button>
      ) : (
        <p className="text-sm text-muted-foreground">
          Pruning is off. Nothing is deleted — set RETENTION_ENABLED on the server to allow it.
        </p>
      )}
    </>
  );
}
